import { cssVar } from './utils.js';

function random(max) {
  return Math.random() * max;
}

function randomSigned(max = 1) {
  const sign = Math.random() < 0.5 ? -1 : 1;
  return (Math.random()) * max  * sign;
}

function gradientFromRedToBlue(strength, lightness) {
    const hue = 240 - (strength + 1) * 120; // From red (0) to blue (1)
    return `hsl(${hue}, 100%, ${lightness || 60}%)`;
}


export class FluxAnimation {
  constructor({ canvas, fluxStrength = 0 }) {
    this.canvas = canvas
    this.fluxStrength = fluxStrength;
    this.ctx = canvas.getContext('2d');
    this.isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    this.updateCanvasSize();

    this.time = 0;

    this.numEllipses = 20; // More ellipses
    this.fluxAmplitude = 10;
    this.fluxSpeed = 0.02;
    this.phaseShiftFactor = 0.2;

    this.ellipses = [];
    this.setupEllipses();

    window.addEventListener('resize', this);
  }

  updateCanvasSize() {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const width = window.innerWidth; // Use parent element's width
    const height = rect.height;

    // console.log({width, height, dpr});

    // Set the actual device pixel dimensions of the canvas
    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;

    // Keep track of the CSS dimensions
    this.canvas.style.width = '100%';

    // Scale all drawing operations by the device pixel ratio
    this.ctx.scale(dpr, dpr);

    // Store these for convenience when drawing
    this.displayWidth = width;
    this.displayHeight = height;
  }

  handleEvent(event) {
    if (event.type === 'resize') this.updateCanvasSize();
  }

  setupEllipses() {
    for (let i = 0; i < this.numEllipses; i++) {
      this.ellipses.push({
        baseRx: 40 + i * 3, // More horizontal
        baseRy: 4 + i * 0.2,   // Fit within canvas
        rotation: Math.random() * 0.2 * Math.PI - Math.random() * 0.5, // Random initial rotation
        rotationSpeed: Math.random() * 0.1 * 0.1, // Random rotation speed
        colorOffset: randomSigned(0.07), // Random color offset for variety
        lineWidth: Math.random() * 2 + 1 // Slightly thicker lines for more visibility
      });
    }
  }

  draw() {
    if (!this.canvas || !this.ctx) return;

    // Clear canvas with black background
    this.ctx.fillStyle = cssVar('--app-background-color');
    this.ctx.fillRect(0, 0, this.displayWidth, this.displayHeight);

    const centerX = this.displayWidth / 2;
    const centerY = this.displayHeight / 2;

    this.mainColor = gradientFromRedToBlue(-this.fluxStrength);
    this.onDraw?.();

    this.ellipses.forEach((ellipse, index) => {
      const idlePhase = this.time * 0.01 + index * this.phaseShiftFactor;
      const idlePulse = Math.sin(idlePhase) * this.fluxAmplitude * 0.4;

      const activePhase = this.time * this.fluxSpeed * this.fluxStrength + index * 4 * this.phaseShiftFactor;
      const activePulse = Math.sin(activePhase) * this.fluxAmplitude * Math.abs(this.fluxStrength);

      const radiusOffset = idlePulse + activePulse;

      const chaos = this.fluxStrength * 4;
      const currentRx = Math.max(10, ellipse.baseRx + radiusOffset);
      const currentRy = Math.max(Math.abs(this.fluxStrength * 20), ellipse.baseRy + radiusOffset * chaos);

      // Update rotation
      // ellipse.rotation += ellipse.rotationSpeed * fluxStrength;

      // Color and glow
      this.ctx.strokeStyle = gradientFromRedToBlue(ellipse.colorOffset * 6 - this.fluxStrength);
      this.ctx.shadowColor = gradientFromRedToBlue(ellipse.colorOffset * 6 - this.fluxStrength, this.isDark ? null : 80);
      this.ctx.shadowBlur = 30;
      this.ctx.lineWidth = ellipse.lineWidth || 2;

      this.ctx.beginPath();
      this.ctx.ellipse(centerX, centerY, currentRx, currentRy, ellipse.rotation, 0, 2 * Math.PI);
      this.ctx.stroke();
    });

    // Reset shadow for next frame elements that shouldn't glow (like UI)
    this.ctx.shadowBlur = 0;

    this.time++;
    requestAnimationFrame(() => this.draw());
  }

  start() {
    this.draw();
  }

  destroy() {
    window.removeEventListener('resize', this);
    this.canvas = null;
    this.ctx = null;
    this.ellipses = [];
  }
}
